/**
 * Capped page size requested when a date range is active, so client-side range
 * narrowing isn't defeated by server pagination. The mock router has no range
 * matcher (see docs); for large libraries the correct fix is a server-side
 * createdFrom/createdTo reserved param.
 */
export const DATE_RANGE_PAGE_SIZE = 1000;

/**
 * Default page size for a normal (non-range) browse list when the caller
 * supplies none — raises the effective cap above the router's default of 25 so
 * the gallery shows essentially everything at mock scale.
 */
export const DEFAULT_LIST_PAGE_SIZE = 500;

/** Suffixes the daterange filter driver appends to its URL keys. */
const DATE_FROM_SUFFIX = '_from';
const DATE_TO_SUFFIX = '_to';

/**
 * Split a flat filter object into server-bound params and any client-side date
 * ranges (keyed by the base field, e.g. `created`). The daterange filter driver
 * serializes `[from, to]` as `${key}_from` / `${key}_to`; the generic router has
 * no range matcher, so those keys must NOT reach the server.
 *
 * @param {Record<string, unknown>} filters
 * @returns {{ params: Record<string, string | string[]>, ranges: { field: string, from: string, to: string }[] }}
 */
function splitFilters(filters) {
  /** @type {Record<string, string | string[]>} */
  const params = {};
  /** @type {Map<string, { from: string, to: string }>} */
  const rangeMap = new Map();

  for (const [key, value] of Object.entries(filters)) {
    if (value == null) continue;

    if (key.endsWith(DATE_FROM_SUFFIX) || key.endsWith(DATE_TO_SUFFIX)) {
      const isFrom = key.endsWith(DATE_FROM_SUFFIX);
      const field = key.slice(0, key.length - (isFrom ? DATE_FROM_SUFFIX : DATE_TO_SUFFIX).length);
      const entry = rangeMap.get(field) ?? { from: '', to: '' };
      const v = String(value);
      if (isFrom) entry.from = v; else entry.to = v;
      rangeMap.set(field, entry);
      continue;
    }

    if (Array.isArray(value)) {
      const clean = value.filter(v => v != null && String(v).trim() !== '').map(v => String(v));
      if (clean.length > 0) params[key] = clean;
    } else if (String(value).trim() !== '') {
      params[key] = String(value);
    }
  }

  const ranges = [...rangeMap.entries()]
    .map(([field, { from, to }]) => ({ field, from, to }))
    .filter(r => r.from !== '' || r.to !== '');

  return { params, ranges };
}

/**
 * Keep only rows whose `field` date falls within [from, to] (inclusive).
 * Empty bound = open on that side. ISO date strings ("yyyy-MM-dd") compare
 * lexicographically, so plain string comparison is correct.
 *
 * @param {import('../contracts/file-selection.js').FileMeta[]} rows
 * @param {{ field: string, from: string, to: string }[]} ranges
 * @returns {import('../contracts/file-selection.js').FileMeta[]}
 */
function applyRanges(rows, ranges) {
  if (ranges.length === 0) return rows;
  return rows.filter(row =>
    ranges.every(({ field, from, to }) => {
      const v = /** @type {Record<string, unknown>} */ (row)[field];
      if (typeof v !== 'string' || v === '') return false;
      if (from && v < from) return false;
      if (to && v > to) return false;
      return true;
    })
  );
}

/**
 * Normalize a list query that may be a legacy filters-only object OR a full
 * GridPage query (`{ filters, page, pageSize, sortField, sortDir }`). If
 * `filters` is present, use it; otherwise treat the whole object as filters
 * (excluding the paging/sort keys). pageSize defaults to 500 only when absent.
 *
 * @param {import('../contracts/file-selection.js').FileListQuery} [query]
 * @returns {{ filters: Record<string, unknown>, page: number, pageSize: number, sortField?: string, sortDir?: string }}
 */
function normalizeListQuery(query = {}) {
  const { filters, page, pageSize, sortField, sortDir, ...rest } = query;
  return {
    filters: filters != null ? filters : rest,
    page: typeof page === 'number' ? page : 1,
    pageSize: typeof pageSize === 'number' ? pageSize : DEFAULT_LIST_PAGE_SIZE,
    sortField: typeof sortField === 'string' ? sortField : undefined,
    sortDir: typeof sortDir === 'string' ? sortDir : undefined,
  };
}

/**
 * Slice an array to a 1-indexed page.
 * @template T
 * @param {T[]} rows
 * @param {number} page
 * @param {number} pageSize
 * @returns {T[]}
 */
function slicePage(rows, page, pageSize) {
  const start = Math.max(0, (page - 1) * pageSize);
  return rows.slice(start, start + pageSize);
}

/**
 * Broad REST file-manager client. Injected only into the file-manager page via
 * the registry closure — never into the global PageServices (so the editor path
 * never sees these admin operations).
 *
 * @param {{ apiClient: import('../contracts/api.js').ApiClient, logger?: { warn: (msg: string, ctx?: { data?: unknown }) => void } }} deps
 * @returns {import('../contracts/file-selection.js').FileManagerClient}
 */
export function createRestFileManagerClient({ apiClient, logger }) {
  return {
    async list(query = {}, opts = {}) {
      const { filters, page, pageSize, sortField, sortDir } = normalizeListQuery(query);
      const { params, ranges } = splitFilters(filters);
      const hasRange = ranges.length > 0;

      // Sort always passes through (server sorts before we narrow/slice).
      if (sortField) params.sortField = sortField;
      if (sortDir) params.sortDir = sortDir;

      if (hasRange) {
        // No server range matcher: cap-fetch the first DATE_RANGE_PAGE_SIZE
        // sorted candidates, narrow client-side, then slice to the requested page.
        params.page = '1';
        params.pageSize = String(DATE_RANGE_PAGE_SIZE);
      } else {
        params.page = String(page);
        params.pageSize = String(pageSize); // already defaulted to 500 when absent
      }

      const result = /** @type {{ rows: import('../contracts/file-selection.js').FileMeta[], total: number, page?: number, pageSize?: number }} */ (
        await apiClient.get('/files', { params, signal: opts.signal })
      );

      if (hasRange) {
        if (result.total > DATE_RANGE_PAGE_SIZE) {
          logger?.warn?.('Date-range filtering may be incomplete: server candidate total exceeds cap', {
            data: { total: result.total, cap: DATE_RANGE_PAGE_SIZE },
          });
        }
        const narrowed = applyRanges(result.rows, ranges);
        const sliced = slicePage(narrowed, page, pageSize);
        return { rows: sliced, total: narrowed.length, page, pageSize };
      }

      return { rows: result.rows, total: result.total, page: result.page ?? page, pageSize: result.pageSize ?? pageSize };
    },

    async create(meta, opts = {}) {
      return /** @type {Promise<import('../contracts/file-selection.js').FileMeta>} */ (
        apiClient.post('/files', meta, { signal: opts.signal })
      );
    },

    async remove(id, opts = {}) {
      await apiClient.delete(`/files/${encodeURIComponent(String(id))}`, undefined, { signal: opts.signal });
    },

    async listTags(opts = {}) {
      const result = /** @type {{ rows: { id: string|number, name: string }[] }} */ (
        await apiClient.get('/file-tags', { params: { pageSize: '500' }, signal: opts.signal })
      );
      return result.rows;
    },

    async createTag(name, opts = {}) {
      return /** @type {Promise<{ id: string|number, name: string }>} */ (
        apiClient.post('/file-tags', { name }, { signal: opts.signal })
      );
    },

    async updateTags(id, tags, opts = {}) {
      return /** @type {Promise<import('../contracts/file-selection.js').FileMeta>} */ (
        apiClient.patch(`/files/${encodeURIComponent(String(id))}`, { tags }, { signal: opts.signal })
      );
    },
  };
}
